import { getLocalStorageValue } from 'technical/local-storage';
import {
  LAST_SYNC_TIMESTAMP_KEY,
  SCAN_SITE_KEY,
} from 'technical/local-storage/constants';
import {
  scanSiteSchema,
  ScanMode,
  type ScanSiteSchema,
} from 'technical/local-storage/types';
import { NoScanSiteConfigured } from './components/no-scan-site-configured';
import { useTranslation } from 'translations/hooks';
import { ScanModeCard } from './components/scan-mode-card';
import { Routes } from 'business/router/constants';
import { Content } from 'ui/components/content';
import { Button } from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { useSyncData } from 'business/scan-site-settings/hooks/use-sync-data';
import { useNetworkStatus } from 'technical/network/use-network-status';
import { NoConnexion } from 'ui/icons/no-connexion';
import { db } from 'technical/db';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import z from 'zod';
import dayjs from 'dayjs';

export const HomePage = () => {
  const { t } = useTranslation();

  const scanSite = getLocalStorageValue<ScanSiteSchema>({
    key: SCAN_SITE_KEY,
    schema: scanSiteSchema,
  });

  const { download, isDownloading } = useSyncData();
  const { isOnline } = useNetworkStatus();
  const { key: locationKey } = useLocation();

  const lastSyncTimestamp = getLocalStorageValue({
    key: LAST_SYNC_TIMESTAMP_KEY,
    schema: z.string(),
  });
  const isDownloadNeeded =
    !lastSyncTimestamp || dayjs().diff(dayjs(lastSyncTimestamp), 'day') >= 7;

  useEffect(() => {
    const openDb = async () => {
      await db.open();
    };

    openDb();
  }, []);

  useEffect(() => {
    if (isOnline && scanSite?.scanMode === ScanMode.TUNNEL) {
      download();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey, download]);

  if (!scanSite?.scanMode) {
    return <NoScanSiteConfigured />;
  }

  return (
    <Content className="justify-start p-8">
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-6">
        <h1 className="text-primary max-w-xs text-center text-lg">
          {t('home.startScan')}
        </h1>
        {scanSite.scanMode === ScanMode.RECEIPT ? (
          <ScanModeCard
            title={t('home.scanMode.receipt.title')}
            subtitle={
              scanSite.isQuarantineEnabled
                ? t('home.scanMode.receipt.subTitle')
                : t('home.scanMode.receipt.subTitleWithoutQuarantine')
            }
            redirection={Routes.Receipt}
          />
        ) : (
          <ScanModeCard
            title={t('home.scanMode.tunnel.title')}
            subtitle={t('home.scanMode.tunnel.subTitle')}
            redirection={Routes.Tunnel}
          />
        )}
      </div>
      {scanSite.scanMode === ScanMode.TUNNEL && isDownloadNeeded && (
        <div className="flex w-full flex-col items-center gap-2">
          {isOnline ? (
            <>
              <p className="text-center text-sm text-gray-400">
                {t('home.prepareOfflineSession.description')}
              </p>
              <Button
                color="primary"
                variant="outlined"
                icon={<CloudDownloadOutlined />}
                className="h-11 w-full"
                onClick={() => {
                  download();
                }}
                loading={isDownloading}
                disabled={!db.isOpen}
              >
                {t('home.prepareOfflineSession.button')}
              </Button>
            </>
          ) : (
            <>
              <NoConnexion />
              <p className="text-center text-sm text-gray-400">
                {t('home.prepareOfflineSession.offlineWarning')}
              </p>
            </>
          )}
        </div>
      )}
    </Content>
  );
};
